import clsx from 'clsx';
import type { ReactNode } from 'react';

import { SDSSkeletonText } from '@repo/sds/components/_internal/SDSSkeletonText';

import '@repo/sds/components/SDSText/SDSText.scss';

export type SDSTextType =
  | 'title'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'body-extra-large'
  | 'body-large-bold'
  | 'body-medium-large-bold'
  | 'body-large'
  | 'body-medium-bold'
  | 'body-medium'
  | 'body-small'
  | 'button-x-large'
  | 'button-large'
  | 'button-medium'
  | 'button-small'
  | 'tag';

type SDSBrandTextColor = 'primary' | 'secondary' | 'tertiary' | 'quaternary';
type SDSStatusTextColor = 'alert' | 'info' | 'warning';
type SDSRoleStatusTextColor = 'status-admin' | 'status-agent' | 'status-mentor' | 'status-student';
type SDSRegularTextColor =
  | 'text-primary'
  | 'text-secondary'
  | 'inactive'
  | 'muted'
  | 'on-brand'
  | 'chip-checkmmark'
  | 'no-answer-scenario';
type SDSAuxiliaryTextColor = 'sidebar-default' | 'sidebar-selected';
export type SDSTextColor =
  | SDSBrandTextColor
  | SDSStatusTextColor
  | SDSRoleStatusTextColor
  | SDSRegularTextColor
  | SDSAuxiliaryTextColor
  | 'inherit';

export type SDSTextAlign = 'left' | 'center' | 'right';
export type SDSTextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';

export interface SDSTextProps {
  id?: string;
  color?: SDSTextColor;
  align?: SDSTextAlign;
  text: ReactNode;
  type: SDSTextType;
  isInline?: boolean;
  className?: string;
  maxRows?: number;
  dataTestId?: string;
  isLoading?: boolean;
  as?: SDSTextElement;
}

// Tag mapping for text types
const TEXT_TAG_MAP: Record<SDSTextType, SDSTextElement> = {
  title: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  'body-extra-large': 'p',
  'body-large-bold': 'p',
  'body-medium-large-bold': 'p',
  'body-large': 'p',
  'body-medium-bold': 'p',
  'body-medium': 'p',
  'body-small': 'p',
  'button-small': 'span',
  'button-medium': 'span',
  'button-large': 'span',
  'button-x-large': 'span',
  tag: 'span',
};

const getTextTag = (type: SDSTextType, isInline: boolean | undefined, as?: SDSTextElement): SDSTextElement => {
  if (isInline) return 'span';
  return as ?? TEXT_TAG_MAP[type] ?? 'span';
};

/**
 * @description SSR Optimized component
 */
// Helper to build text className
const buildTextClassName = (
  type: SDSTextType,
  align: SDSTextAlign,
  isInline?: boolean,
  color?: SDSTextColor,
  maxRows?: number,
  className?: string,
) => {
  return clsx(
    'SDSText',
    {
      [`SDSText--${type}`]: type,
      [`SDSText--${align}`]: align,
      'SDSText--inline': isInline,
      [`SDSText--${color}`]: color,
      [`SDSText--ellipsis-${maxRows}`]: typeof maxRows === 'number',
    },
    className,
  );
};

// Helper to render text element
const renderTextElement = (
  tag: SDSTextElement,
  props: { 'data-testid'?: string; className: string; id?: string },
  text: ReactNode,
) => {
  switch (tag) {
    case 'h1':
      return <h1 {...props}>{text}</h1>;
    case 'h2':
      return <h2 {...props}>{text}</h2>;
    case 'h3':
      return <h3 {...props}>{text}</h3>;
    case 'h4':
      return <h4 {...props}>{text}</h4>;
    case 'h5':
      return <h5 {...props}>{text}</h5>;
    case 'h6':
      return <h6 {...props}>{text}</h6>;
    case 'p':
      return <p {...props}>{text}</p>;
    default:
      return <span {...props}>{text}</span>;
  }
};

export function SDSText(props: SDSTextProps) {
  const {
    id,
    color = 'text-primary',
    text,
    type,
    align = 'left',
    isInline,
    className,
    maxRows,
    dataTestId,
    isLoading = false,
    as,
  } = props;

  if (isLoading) {
    const lines = typeof maxRows === 'number' ? maxRows : 1;
    return <SDSSkeletonText textType={type} lines={lines} className={className} />;
  }

  const Tag = getTextTag(type, isInline, as);
  const textClassName = buildTextClassName(type, align, isInline, color, maxRows, className);

  const elementProps = {
    id,
    'data-testid': dataTestId,
    className: textClassName,
  };

  return renderTextElement(Tag, elementProps, text);
}
